import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CCol, CRow, CForm, CFormInput, CInputGroup, CInputGroupText, CFormLabel, CFormSelect, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import CIcon from '@coreui/icons-react'
import { cilDollar } from '@coreui/icons'
import { CCard, CCardHeader, CCardBody } from '@coreui/react'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import { usePendiente } from '../components/ww/pendiente'

const columnas: WwColumn[] = [
  { key: 'ingresosNro', title: 'Nro.', type: 'number' },
  { key: 'ingresosFecha', title: 'Fecha', type: 'date' },
  { key: 'ingresosTipo', title: 'Tipo' },
  { key: 'socioNombre', title: 'Socio Nombre' },
  { key: 'socioEstado', title: 'Socio Estado' },
  { key: 'socioTotalInversion', title: 'Socio Total Inversion', type: 'currency' },
  { key: 'cajaNombre', title: 'Caja Nombre' },
  { key: 'ingresosValor', title: 'Valor', type: 'currency' },
  { key: 'ingresosObservacion', title: 'Observacion' },
]

const IngresosPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // Catálogo de socios (el select muestra el NOMBRE, el backend recibe el código).
  const [socios, setSocios] = useState<{ socioCodigo: number; socioNombre: string }[]>([])
  const [cajas, setCajas] = useState<{ cajaNro: number; cajaNombre: string }[]>([])
  useEffect(() => {
    api.get<any[]>('/socios?empresa=' + empresa).then(setSocios).catch(() => undefined)
    api.get<any[]>(`/cajas/${empresa}/resumen`).then(setCajas).catch(() => undefined)
  }, [empresa])
  // ── Filtros rápidos (WwFilterBar) ──
  const [filtroTipo, setFiltroTipo] = useState('')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Fila en edición (ingresosww.cs 'DO UPDATE' → webobject ingresos Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [errorModal, setErrorModal] = useState('')
  const [form, setForm] = useState({
    cajaNro: '', socioCodigo: '', tipo: 1, valor: '', observacion: '',
  })

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroTipo && String(row.ingresosTipo ?? '').trim() !== filtroTipo) return false
    return true
  }), [rows, filtroTipo])
  const tiposIngreso = useMemo(() => [...new Set(rows.map((r) => String(r.ingresosTipo ?? '').trim()).filter(Boolean))], [rows])
  const chips = [filtroTipo ? { clave: 'tipo', label: `Tipo: ${filtroTipo}` } : null].filter(Boolean) as { clave: string; label: string }[]
  const limpiarFiltros = () => setFiltroTipo('')
  const quitarFiltro = (clave: string) => { if (clave === 'tipo') setFiltroTipo('') }
  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/ingresos?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  const abrirNuevo = () => {
    setEditando(null)
    setForm({
      cajaNro: '', socioCodigo: '', tipo: 1, valor: '', observacion: '',
    })
    setAbierto(true)
  }

  // Modificar: carga la fila en el form y abre el modal. Campos editables
  // del webobject (ingresos.cs:3032-3054: Tipo combo, Valor, Observacion);
  // CajaNro/SocioCodigo deshabilitados en UPD (ingresos.cs:1433-1442).
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      cajaNro: String(fila.cajaNro ?? ''),
      socioCodigo: String(fila.socioCodigo ?? ''),
      tipo: Number(fila.ingresosTipo ?? 1),
      valor: String(fila.ingresosValor ?? ''),
      observacion: fila.ingresosObservacion ?? '',
    })
    setAbierto(true)
  }

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    setGuardando(true)
    try {
      if (editando) {
        // DO UPDATE: webobject ingresos Mode="UPD" con IngresosNro (la llave
        // viaja en la URL). El backend replica BC000J14 (ingresos_bc.cs:1975)
        // y recalcula caja/socio (ingresos_bc.cs:735-745).
        await api.put(`/ingresos/${editando.ingresosNro}`, {
          empresaCodigo: empresa,
          ingresosTipo: Number(form.tipo) || 0,
          valor: Number(form.valor),
          observacion: form.observacion || undefined,
        })
      } else {
        await api.post('/ingresos', {
          empresaCodigo: empresa,
          cajaNro: Number(form.cajaNro),
          socioCodigo: Number(form.socioCodigo),
          valor: Number(form.valor),
          ingresosTipo: Number(form.tipo) || 0,
          observacion: form.observacion || undefined,
        })
      }
      setAbierto(false)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  const eliminar = (fila: any) => {
    // DO DELETE: webobject ingresos Mode="DLT" con IngresosNro. El backend
    // replica BC000J15 (ingresos_bc.cs:1976) y revierte caja/socio
    // (ingresos_bc.cs:712-731) — sin control de referencias (hoja).
    if (!window.confirm(`¿Eliminar el ingreso ${fila.ingresosNro}?`)) return
    api.delete(`/ingresos/${fila.ingresosNro}?empresa=${empresa}`)
      .then(() => {
        setAviso('Ingreso eliminado.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Ingresos</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}
      <WwGrid
        filters={
          <WwFilterBar
            selects={[
              { key: 'tipo', label: 'Todos los Tipos', opciones: tiposIngreso, valor: filtroTipo, onChange: setFiltroTipo },
            ]}
            chips={chips}
            onQuitar={quitarFiltro}
            onLimpiar={limpiarFiltros}
          />
        }
        title="Ingresos"
        columns={columnas}
        rows={visibleRows}
        busquedaPlaceholder="Buscar por ingreso, socio o caja..." 
        onTotalChange={setTotalReg}
        loading={cargando}
        onAgregar={abrirNuevo}
        onEditar={abrirEditar}
        onEliminar={eliminar}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar ingreso de capital' : 'Registrar ingreso de capital'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={guardar}>
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilDollar} size="sm" /> Datos del Ingreso (Capital)
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  {editando && (
                    <CCol md={6}><CFormLabel>Nro</CFormLabel><CFormInput value={editando.ingresosNro} disabled /></CCol>
                  )}
                  {editando && (
                    <CCol md={6}><CFormLabel>Fecha</CFormLabel><CFormInput type="date" value={String(editando.ingresosFecha ?? '').slice(0, 10)} disabled /></CCol>
                  )}
                  <CCol md={6}>
                    {/* cmbIngresosTipo del GX (ingresosww.cs:2979): "1" = Capital */}
                    <CFormLabel>Tipo</CFormLabel>
                    <CFormSelect value={String(form.tipo)} onChange={(e) => setForm({ ...form, tipo: Number(e.target.value) || 1 })}>
                      <option value="1">Capital</option>
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    {/* Caja por NOMBRE (el backend recibe cajaNro) */}
                    <CFormLabel>Caja</CFormLabel>
                    <CFormSelect value={form.cajaNro} disabled={editando !== null} onChange={(e) => setForm({ ...form, cajaNro: e.target.value })}>
                      <option value="">Seleccione...</option>
                      {cajas.map((cj) => <option key={cj.cajaNro} value={cj.cajaNro}>{cj.cajaNombre}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    {/* Select del SOCIO por NOMBRE (el backend recibe socioCodigo) */}
                    <CFormLabel>Socio</CFormLabel>
                    <CFormSelect value={form.socioCodigo} disabled={editando !== null} onChange={(e) => setForm({ ...form, socioCodigo: e.target.value })}>
                      <option value="">Seleccione...</option>
                      {socios.map((s) => <option key={s.socioCodigo} value={s.socioCodigo}>{s.socioNombre}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Valor</CFormLabel>
                    {/* [RGM] 2026-10-02 (moneda global, pedido Rafael): prefijo
                        de la moneda configurada + formato de miles en vivo. */}
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.valor}
                        onValueChange={(v) => setForm({ ...form, valor: v.value })}
                        thousandSeparator=","
                        decimalSeparator="."
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        inputMode="decimal"
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={12}><CFormLabel>Observación</CFormLabel><CFormInput value={form.observacion} onChange={(e) => setForm({ ...form, observacion: capitalizarPalabras(e.target.value) })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Registrar ingreso'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default IngresosPage
